"use client";

import { useEffect, useRef, type ComponentType, type ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FilePen, FlaskConical, GitFork, Radio, Undo2 } from "lucide-react";
import { AsciiTrail } from "@/components/site/AsciiTrail";
import { cn } from "@/lib/utils";

gsap.registerPlugin(ScrollTrigger);

/**
 * The amendment flow, drawn the way automation builders draw a workflow:
 * a trigger, a test, a human gate, two outcomes. It builds itself node by
 * node the first time it scrolls into view and then stays built.
 *
 * The one thing that keeps moving is the live pill at the top, which is the
 * point of the section: sites are entering data on v1.0 the whole way
 * through, and on v1.1 the moment it is signed.
 *
 * Everything is in the HTML and visible by default. GSAP hides the nodes
 * only once it is ready to reveal them, so a reader who prefers reduced
 * motion, or a script that never runs, still sees the finished diagram.
 */
export function AmendmentFlow() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("is-done");
      return;
    }

    let fallback = 0;
    let later = 0;
    const ctx = gsap.context(() => {
      const nodes = gsap.utils.toArray<HTMLElement>("[data-node]");
      const links = gsap.utils.toArray<HTMLElement>("[data-link]");
      const edges = gsap.utils.toArray<HTMLElement>("[data-edge]");
      const before = el.querySelector<HTMLElement>("[data-pill='before']");
      const after = el.querySelector<HTMLElement>("[data-pill='after']");

      gsap.set(nodes, { autoAlpha: 0, y: 18 });
      gsap.set(links, { scaleY: 0, transformOrigin: "50% 0%" });
      gsap.set("[data-link='bar']", { scaleY: 1, scaleX: 0, transformOrigin: "50% 50%" });
      gsap.set(edges, { autoAlpha: 0, scale: 0.7 });
      if (after) gsap.set(after, { autoAlpha: 0 });

      const tl = gsap.timeline({ paused: true, defaults: { ease: "power2.out" } });
      const node = (id: string) => tl.to(`[data-node='${id}']`, { autoAlpha: 1, y: 0, duration: 0.5 });
      const link = (id: string) => tl.to(`[data-link='${id}']`, { scaleY: 1, duration: 0.3 }, "-=0.1");

      node("amend");
      link("l1");
      node("test");
      link("l2");
      node("review");
      link("stub");
      tl.to("[data-link='bar']", { scaleX: 1, duration: 0.35 });
      tl.to(edges, { autoAlpha: 1, scale: 1, duration: 0.3, stagger: 0.1 });
      tl.to("[data-link='drop']", { scaleY: 1, duration: 0.3, stagger: 0.1 }, "-=0.15");
      tl.to(["[data-node='live']", "[data-node='draft']"], { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.15 });
      if (before && after) {
        tl.to(before, { autoAlpha: 0, y: -6, duration: 0.3 }, "+=0.4");
        tl.to(after, { autoAlpha: 1, duration: 0.4 }, "-=0.1");
      }
      tl.call(() => el.classList.add("is-done"));

      ScrollTrigger.create({ trigger: el, start: "top 78%", once: true, onEnter: () => tl.play() });

      // Two ways the build can fail to happen, and one answer to both: show
      // the finished diagram. If no frames have been drawn, the page is in a
      // background tab or a renderer that never ticks, as PageMotion also
      // guards against. If frames are drawn but the trigger never fired for
      // a panel already on screen, the layout settled oddly.
      const finish = () => {
        if (tl.progress() === 0 && !tl.isActive()) tl.progress(1);
      };
      const startFrame = gsap.ticker.frame;
      fallback = window.setTimeout(() => {
        if (gsap.ticker.frame - startFrame < 10) {
          finish();
          return;
        }
        later = window.setTimeout(() => {
          if (el.getBoundingClientRect().top < window.innerHeight * 0.9) finish();
        }, 3200);
      }, 1800);
    }, el);

    return () => {
      window.clearTimeout(fallback);
      window.clearTimeout(later);
      ctx.revert();
    };
  }, []);

  return (
    <div ref={root} className="amend" data-trail="off" aria-label="How a mid-study amendment goes live">
      {/* The pointer trail draws under the cards, never over them. */}
      <AsciiTrail className="amend-canvas" />
      <div className="amend-top">
        <span className="amend-pill" data-pill="before">
          <i className="amend-dot" aria-hidden="true" />
          LIVE · v1.0 · Site 04, Visit 3 · entering data
        </span>
        <span className="amend-pill" data-pill="after">
          <i className="amend-dot" aria-hidden="true" />
          LIVE · v1.1 · effective 12:05 · sites uninterrupted
        </span>
      </div>

      <div className="amend-flow">
        <Node id="amend" label="Amendment" icon={FilePen} title="Protocol amended" chip="Draft" time="09:12">
          v1.1 is drafted beside the live study. v1.0 keeps running at every site.
        </Node>
        <span className="amend-link" data-link="l1" aria-hidden="true" />
        <Node id="test" label="Test" icon={FlaskConical} title="Test the change" chip="Test subjects" time="10:40">
          Only what changed is exercised: entry, checks, derivations and export. Test data never touches production.
        </Node>
        <span className="amend-link" data-link="l2" aria-hidden="true" />
        <Node id="review" label="Review" icon={GitFork} title="A data manager reviews the delta" chip="Sign" time="12:05">
          Reviewer, timestamp and the value before and after go to the audit trail.
        </Node>

        <div className="amend-branch" aria-hidden="true">
          <span className="amend-link amend-stub" data-link="stub" />
          <span className="amend-bar" data-link="bar" />
          <span className="amend-edge amend-edge-l" data-edge>
            Signed
          </span>
          <span className="amend-edge amend-edge-r" data-edge>
            Not signed
          </span>
          <span className="amend-link amend-drop amend-drop-l" data-link="drop" />
          <span className="amend-link amend-drop amend-drop-r" data-link="drop" />
        </div>

        <div className="amend-outcomes">
          <Node id="live" edge="Signed" icon={Radio} title="Promote to live" chip="Live" time="12:05" live>
            v1.1 takes effect from its date. Sites keep entering, nothing is re-entered, nothing migrates.
          </Node>
          <Node id="draft" edge="Not signed" icon={Undo2} title="Back to draft" chip="Draft">
            Nothing reaches a site without a qualified name on it. The live version is untouched.
          </Node>
        </div>
      </div>
    </div>
  );
}

function Node({
  id,
  label,
  edge,
  icon: Icon,
  title,
  chip,
  time,
  live,
  children,
}: {
  id: string;
  /** The tab above the card, as a workflow builder labels its node types. */
  label?: string;
  /** The branch this outcome sits on, shown on phones where the lines are not drawn. */
  edge?: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  chip: string;
  time?: string;
  live?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("amend-node", live && "amend-node-live")} data-node={id}>
      {label && <span className="amend-label">{label}</span>}
      {edge && <span className="amend-edge-m">{edge}</span>}
      <div className="amend-card">
        <div className="amend-head">
          <Icon className="amend-icon" />
          <span className="amend-title">{title}</span>
          <span className="amend-chip">{chip}</span>
        </div>
        <p className="amend-body">
          {children}
          {time && <span className="amend-time">{time}</span>}
        </p>
      </div>
    </div>
  );
}
